import { optionalProp } from '../../core';
import { type PaginationProps } from '../types/propType';
import { MLPVersion } from '../../types/enums';
import { AdminPaginationStyle } from '../types/enums';
import MLP1Pagination from './MLP1/MLP1Pagination';
import AdminPagination from './AdminPagination/AdminPagination';
import DefaultPagination from './DefaultPagination/DefaultPagination';
import RowPerPageAndNextPage from './RowPerPageAndNextPage/RowPerPageAndNextPage';
import RowPerPageAndAllPages from './RowPerPageAndAllPages/RowPerPageAndAllPages';

const Pagination = ({
  mlpVersion = MLPVersion.ONE,
  adminPaginationStyle = AdminPaginationStyle.ONLY_NEXT_PAGE,
  hasNextPage,
  scrollToTopRefId,
  enableStickyPagination,
  ...rest
}: PaginationProps) => {
  const { totalRecords, currentPage, rowsPerPage, updatePaginationData } = rest;

  const startRecordIndex = (pageIndex: number, rows: number) => {
    const index = (pageIndex + 1) * rows - rows;
    if (index < 0) {
      return 0;
    } else {
      return index;
    }
  };
  const endRecordIndex = (pageIndex: number, rows: number) => {
    const index = (pageIndex + 1) * rows;
    if (index > totalRecords) {
      return totalRecords;
    } else {
      return index;
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 0) {
      const previousPage = currentPage - 1;
      updatePaginationData({
        currentPage: previousPage,
        recordStartIndex: startRecordIndex(previousPage, rowsPerPage),
        recordEndIndex: endRecordIndex(previousPage, rowsPerPage),
      });
      scrollToTop(scrollToTopRefId);
    }
  };

  const goToNextPage = () => {
    const nextPage = currentPage + 1;
    if (mlpVersion === MLPVersion.ONE && adminPaginationStyle === AdminPaginationStyle.ONLY_NEXT_PAGE && hasNextPage) {
      updatePaginationData({
        currentPage: nextPage,
      });
    } else if (currentPage + 1 < Math.ceil(totalRecords / rowsPerPage)) {
      updatePaginationData({
        currentPage: nextPage,
        recordStartIndex: startRecordIndex(nextPage, rowsPerPage),
        recordEndIndex: endRecordIndex(nextPage, rowsPerPage),
      });
    }
    scrollToTop(scrollToTopRefId);
  };

  if (mlpVersion === MLPVersion.ONE) {
    if (adminPaginationStyle === AdminPaginationStyle.WITH_ALL_PAGES) {
      return (
        <AdminPagination
          startRecordIndex={startRecordIndex}
          endRecordIndex={endRecordIndex}
          goToPreviousPage={goToPreviousPage}
          goToNextPage={goToNextPage}
          hasNextPage={hasNextPage as boolean}
          scrollToTopRefId={scrollToTopRefId as string}
          {...optionalProp('enableStickyPagination', enableStickyPagination)}
          {...rest}
        />
      );
    } else if (adminPaginationStyle === AdminPaginationStyle.WITH_ROW_PER_NEXT_PAGE) {
      return (
        <RowPerPageAndNextPage
          goToPreviousPage={goToPreviousPage}
          goToNextPage={goToNextPage}
          startRecordIndex={startRecordIndex}
          endRecordIndex={endRecordIndex}
          scrollToTopRefId={scrollToTopRefId as string}
          adminPaginationStyle={AdminPaginationStyle.WITH_ROW_PER_NEXT_PAGE}
          {...optionalProp('enableStickyPagination', enableStickyPagination)}
          {...rest}
        />
      );
    } else if (adminPaginationStyle === AdminPaginationStyle.WITH_ALL_PAGES_AND_ROW_SELECTOR) {
      return (
        <RowPerPageAndAllPages
          goToPreviousPage={goToPreviousPage}
          goToNextPage={goToNextPage}
          startRecordIndex={startRecordIndex}
          endRecordIndex={endRecordIndex}
          scrollToTopRefId={scrollToTopRefId as string}
          {...optionalProp('enableStickyPagination', enableStickyPagination)}
          {...rest}
        />
      );
    }
    return (
      <MLP1Pagination
        goToPreviousPage={goToPreviousPage}
        goToNextPage={goToNextPage}
        hasNextPage={hasNextPage as boolean}
        scrollToTopRefId={scrollToTopRefId as string}
        {...optionalProp('enableStickyPagination', enableStickyPagination)}
        {...rest}
      />
    );
  } else {
    return (
      <DefaultPagination
        startRecordIndex={startRecordIndex}
        endRecordIndex={endRecordIndex}
        goToPreviousPage={goToPreviousPage}
        goToNextPage={goToNextPage}
        hasNextPage={hasNextPage as boolean}
        scrollToTopRefId={scrollToTopRefId as string}
        {...rest}
      />
    );
  }
};

export const scrollToTop = (backToTopRefId: string | undefined) => {
  if (backToTopRefId) {
    const targetElement = document.getElementById(backToTopRefId);
    if (targetElement) {
      if (targetElement.scrollTo) {
        targetElement.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        targetElement.scrollTop = 0;
      }
    }
  }
};

export default Pagination;
